import type { HomeSlice } from '../bs/home'

/**
 * English for the two home screens.
 *
 * "Report" is the noun throughout, matching the enum labels the server sends
 * for `submissionTypes` — an English-speaking auditor reading "Breakdown or
 * incident" on a badge should meet the same word in the heading above it.
 */
export const home: HomeSlice = {
  'home.greeting': 'Good day, {name}',

  'home.report.title': 'Report a fault or an idea',
  'home.report.text': 'A breakdown, an incident, a safety issue or an improvement',
  'home.scan.title': 'Scan a QR code',
  'home.scan.text': 'Open a report for the machine you are standing at',

  'home.stat.mySubmissions': 'My reports',
  'home.stat.mySubmissions.explain': 'Your reports from the last twelve months.',
  'home.stat.inProgress': 'In progress',
  'home.stat.inProgress.explain':
    'Your reports that are not resolved yet, whenever you sent them.',
  'home.stat.acceptedProposals': 'Accepted proposals',
  'home.stat.acceptedProposals.explain':
    'Proposals the committee accepted — only these raise your rating on the ranking, and a refused proposal takes nothing away.',
  'home.stat.points': 'Points earned',
  'home.stat.points.explain':
    'Points the committee gave your accepted proposals; the rating counts how many were accepted, not how many points they carried.',

  'home.recent.title': 'My latest reports',
  'home.recent.empty': 'You have not sent a report yet',
  'home.recent.emptyText': 'Once you report a fault or send an idea, it appears here.',

  'home.newSubmission': 'New report',
  'home.openQueue': 'Open the queue',

  'home.stat.openSubmissions': 'Open reports',
  'home.stat.openSubmissions.hint': 'Everything not yet resolved',
  'home.stat.openSubmissions.explain': 'Every open report in the plant, not only your own.',
  'home.stat.overdue': 'Overdue',
  'home.stat.overdue.needsAttention': 'Needs attention today',
  'home.stat.overdue.allOnTime': 'All on time',
  'home.stat.overdue.explain':
    'Open reports whose due date has passed; reports with no due date are not counted here.',
  'home.stat.myTasks': 'My tasks',
  'home.stat.myTasks.explain': 'Tasks assigned to you that are not finished yet.',
  'home.stat.fiveSThisMonth': '5S this month',
  'home.stat.fiveS.open': 'Open',
  'home.stat.fiveS.hint': 'Work centre scoring',
  'home.stat.fiveS.notAllowed': 'You cannot score work centres',

  'home.overdue.one': '{count} report is overdue.',
  'home.overdue.few': '{count} reports are overdue.',
  'home.overdue.many': '{count} reports are overdue.',
  'home.overdue.view': 'Look',

  'home.module.queue': 'Queue',
  'home.module.queue.text': 'Unresolved reports, worst first',
  'home.module.tasks': 'Tasks',
  'home.module.tasks.text': 'What is assigned to you and your team',
  'home.module.fiveS': '5S evaluation',
  'home.module.fiveS.text': 'Score a work centre against the criteria',
  'home.module.analytics': 'Analytics',
  'home.module.analytics.text': 'Trends, bottlenecks and 5S results',
  'home.module.admin': 'Administration',
  'home.module.admin.text': 'People, code lists and the change log',

  'home.queue.title': 'Open reports',
  'home.queue.empty': 'No open reports',
  'home.queue.emptyText': 'Every report is resolved or closed.',

  'home.tasks.title': 'My tasks',
  'home.tasks.all': 'All tasks →',
  'home.tasks.due': 'Due: {date}',
  'home.tasks.noDue': 'No due date',
  'home.tasks.overdue': 'Overdue',
  'home.submissions.all': 'All reports →',
  'home.fiveS.title': 'The centres you keep',
  'home.fiveS.plant': 'Plant average',
  'home.fiveS.explain':
    'The plant average beside it is there so your own figure has something to be read against — it is not a ranking and it says nothing about your place.',

  'leaderboard.title': 'Ranking',
  'leaderboard.formula':
    'Base: 5S {fiveS} and finished tasks {tasks}. Accepted proposals add up to +{bonus} ({accepted} accepted for the full bonus) — proposals can only raise a rating, never lower it.',
  'leaderboard.explain.note':
    'The 5S half describes the machines somebody has looked after, not the person — anybody moved onto a neglected centre reads low for exactly that reason. Accepted proposals are added on top of the base and can only raise it: a refused proposal takes nothing away. Somebody who was on no walk, or who was given no task, is rated on the half they do have, and the missing half does not count as a nought.',
  'leaderboard.explain.ties':
    'The same score shares a place: two people on 82 are both second, and the next one is fourth.',
  'leaderboard.part.fiveS': '5S',
  'leaderboard.part.bonus': '+{bonus} for {accepted} accepted',
  'leaderboard.part.followThrough': 'Tasks',
  'leaderboard.you': '(you)',
  'leaderboard.thin': 'few walks',
  'leaderboard.showAll': 'Show all ({count})',
  'leaderboard.showLess': 'Show fewer',
  'leaderboard.rating': 'Rating',
  'leaderboard.position': 'Place',
}
